import type { Loan } from 'app/types/loan';
import { LoanStatus } from 'app/types/loan';
import { addDays, isAfter, isBefore, isSameDay, startOfDay, startOfMonth, subDays } from 'app/lib/date';
import { differenceInCalendarMonths, endOfMonth } from 'date-fns';

export function getLoanCalendarEnd(loan: Pick<Loan, 'end' | 'returned' | 'status'>, today = new Date()) {
  const plannedOrActualEnd = startOfDay(new Date(loan.returned ?? loan.end));

  if (loan.status === LoanStatus.Accepted && !loan.returned && isBefore(plannedOrActualEnd, startOfDay(today))) {
    return startOfDay(today);
  }

  return plannedOrActualEnd;
}

export function loanOccursOnDate(
  loan: Pick<Loan, 'start' | 'end' | 'returned' | 'status'>,
  date: Date,
  today = new Date(),
) {
  const day = startOfDay(date);
  const start = startOfDay(new Date(loan.start));
  const end = getLoanCalendarEnd(loan, today);

  return isSameDay(day, start) || isSameDay(day, end) || (isAfter(day, start) && isBefore(day, end));
}

export function loanIntersectsMonth(
  loan: Pick<Loan, 'start' | 'end' | 'returned' | 'status'>,
  month: Date,
  today = new Date(),
) {
  const monthStart = startOfMonth(month);
  const monthEnd = startOfDay(endOfMonth(month));
  const loanStart = startOfDay(new Date(loan.start));
  const loanEnd = getLoanCalendarEnd(loan, today);

  return !isAfter(loanStart, monthEnd) && !isBefore(loanEnd, monthStart);
}

export function getCalendarGridDays(month: Date) {
  const monthStart = startOfMonth(month);
  const firstGridDay = subDays(monthStart, monthStart.getDay());

  return Array.from({ length: 42 }, (_, index) => addDays(firstGridDay, index));
}

export function getClosestActivityMonth(
  loans: Pick<Loan, 'start' | 'end' | 'returned' | 'status'>[],
  month: Date,
  today = new Date(),
) {
  const currentMonth = startOfMonth(month);
  const currentMonthTime = currentMonth.getTime();
  const candidateMonths: Date[] = [];
  for (const loan of loans) {
    const loanMonths = [startOfMonth(new Date(loan.start)), startOfMonth(getLoanCalendarEnd(loan, today))];
    for (const candidate of loanMonths) {
      if (candidate.getTime() !== currentMonthTime) candidateMonths.push(candidate);
    }
  }

  if (!candidateMonths.length) {
    return null;
  }

  return candidateMonths.reduce((closest, candidate) =>
    Math.abs(differenceInCalendarMonths(candidate, currentMonth)) <
    Math.abs(differenceInCalendarMonths(closest, currentMonth))
      ? candidate
      : closest,
  );
}
